import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/shared/components/ui/dialog';
import { toast } from 'sonner';
import { History, ArrowLeft, RotateCcw, CheckCircle, Clock, User, FileText } from 'lucide-react';
import useLandingConfig from '../hooks/useLandingConfig';

export const VersionHistory = () => {const { t } = useTranslation("landing");
  const { loading, getVersions, rollback, getAuditLogs } = useLandingConfig();
  const [versions, setVersions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [isRollbackOpen, setIsRollbackOpen] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      const versionsResult = await getVersions();
      if (versionsResult.success) {
        setVersions(versionsResult.versions);
      }

      const logsResult = await getAuditLogs({ limit: 50 });
      if (logsResult.success) {
        setAuditLogs(logsResult.logs);
      }
    };
    loadData();
  }, [getVersions, getAuditLogs]);

  const handleRollback = async () => {
    if (!selectedVersion) return;

    setRollbackLoading(true);
    const result = await rollback(selectedVersion.id);
    setRollbackLoading(false);

    if (result.success) {
      toast.success(`Rolled back to ${selectedVersion.version_label}`);
      setIsRollbackOpen(false);
      // Refresh versions
      const versionsResult = await getVersions();
      if (versionsResult.success) {
        setVersions(versionsResult.versions);
      }
    } else {
      toast.error(result.error || 'Failed to rollback');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>);

  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <a href="/admin/landing">
            <ArrowLeft className="w-5 h-5" />
          </a>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <History className="w-6 h-6 text-orange-500" />{t("versionHistory", "Version History")}

          </h1>
          <p className="text-slate-400">{t("viewPublishedVersions", "View published versions and rollback if needed")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Versions List */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">{t("publishedVersions", "Published Versions")}</CardTitle>
            <CardDescription className="text-slate-400">
              {versions.length}{t("version", "version")}{versions.length !== 1 ? 's' : ''}{t("available", "available")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {versions.length === 0 ?
            <div className="text-center py-8">
                <FileText className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400">{t("noVersionsPublished", "No versions published yet")}</p>
                <p className="text-sm text-slate-500 mt-2">{t("publishYourLanding", "Publish your landing page to create the first version")}</p>
              </div> :

            <div className="space-y-3">
                {versions.map((version) =>
              <div
                key={version.id}
                className={`p-4 rounded-lg border ${
                version.is_published ?
                'bg-green-500/10 border-green-500/30' :
                'bg-slate-700/30 border-slate-600'}`
                }
                data-testid={`version-${version.version_number}`}>
                
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    version.is_published ? 'bg-green-500/20' : 'bg-slate-600'}`
                    }>
                          {version.is_published ?
                      <CheckCircle className="w-5 h-5 text-green-500" /> :

                      <FileText className="w-5 h-5 text-slate-400" />
                      }
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-white">{version.version_label}</h4>
                            {version.is_published &&
                        <Badge className="bg-green-500/20 text-green-500 border-0">{t("current", "Current")}</Badge>
                        }
                          </div>
                          <p className="text-sm text-slate-400">{t("version", "Version")}
                        {version.version_number}
                          </p>
                        </div>
                      </div>
                      {!version.is_published &&
                  <Dialog open={isRollbackOpen && selectedVersion?.id === version.id} onOpenChange={(open) => {
                    setIsRollbackOpen(open);
                    if (open) setSelectedVersion(version);
                  }}>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" className="gap-2">
                              <RotateCcw className="w-4 h-4" />{t("rollback", "Rollback")}

                      </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-slate-800 border-slate-700">
                            <DialogHeader>
                              <DialogTitle className="text-white">{t("confirmRollback", "Confirm Rollback")}</DialogTitle>
                              <DialogDescription className="text-slate-400">{t("areYouSure", "Are you sure you want to rollback to")}
                          <strong>{version.version_label}</strong>{t("thisWillReplace", "?\n                                This will replace your current landing page configuration.")}

                        </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setIsRollbackOpen(false)}>{t("cancel", "Cancel")}</Button>
                              <Button
                          onClick={handleRollback}
                          disabled={rollbackLoading}
                          className="bg-orange-600 hover:bg-orange-700">
                          
                                {rollbackLoading ? 'Rolling back...' : 'Confirm Rollback'}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                  }
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-600/50">
                      <div className="flex items-center gap-4 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {version.published_at ? formatDate(version.published_at) : formatDate(version.created_at)}
                        </span>
                        {version.notes &&
                    <span className="truncate max-w-[200px]">{version.notes}</span>
                    }
                      </div>
                    </div>
                  </div>
              )}
              </div>
            }
          </CardContent>
        </Card>

        {/* Audit Log */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">{t("auditTrail", "Audit Trail")}</CardTitle>
            <CardDescription className="text-slate-400">{t("recentChangesTo", "Recent changes to your landing page")}

            </CardDescription>
          </CardHeader>
          <CardContent>
            {auditLogs.length === 0 ?
            <div className="text-center py-8">
                <Clock className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400">{t("noActivityRecorded", "No activity recorded yet")}</p>
              </div> :

            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                {auditLogs.map((log, index) =>
              <div
                key={index}
                className="p-3 rounded-lg bg-slate-700/30 border border-slate-600/50">
                
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                  log.action === 'publish' ? 'bg-green-500' :
                  log.action === 'create' ? 'bg-blue-500' :
                  log.action === 'update' ? 'bg-yellow-500' :
                  log.action === 'delete' ? 'bg-red-500' :
                  log.action === 'rollback' ? 'bg-orange-500' : 'bg-slate-500'}`
                  } />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white">{log.description}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {log.user_email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(log.timestamp)}
                          </span>
                        </div>
                        {log.entity_key &&
                    <Badge variant="outline" className="mt-2 text-xs">
                            {log.entity_type}: {log.entity_key}
                          </Badge>
                    }
                      </div>
                    </div>
                  </div>
              )}
              </div>
            }
          </CardContent>
        </Card>
      </div>
    </div>);

};

export default VersionHistory;